import { useState, useEffect, useCallback, useRef } from 'react';
import * as JsSIP from 'jssip';

export interface WebPhoneCredentials {
  username: string; // custid_extension
  password: string;
  extension: string;
  displayName: string;
  sbcServers: Array<{
    host: string;
    wsUri: string;
    priority: number;
  }>;
}

export interface CallSession {
  session: any;
  direction: 'incoming' | 'outgoing';
  remoteNumber: string;
  startTime: Date;
  duration: number;
  status: 'ringing' | 'connected' | 'ended';
}

export interface RTCStats {
  bytesReceived: number;
  bytesSent: number;
  receiveBitrate: number;
  sendBitrate: number;
  packetsReceived: number;
  packetsSent: number;
  packetsLost: number;
  packetsLostPercentage: string;
  jitter: string;
  roundTripTime: string;
  codec: string;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'registered' | 'error';
export type CallStatus = 'idle' | 'calling' | 'ringing' | 'in-call' | 'transferring' | 'consulting' | 'consult-connected';

export function useWebPhoneV4() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [currentCall, setCurrentCall] = useState<CallSession | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [dialedNumber, setDialedNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [consultCall, setConsultCall] = useState<CallSession | null>(null);
  const [audioDevices, setAudioDevices] = useState<{ inputDevices: MediaDeviceInfo[], outputDevices: MediaDeviceInfo[] }>({ inputDevices: [], outputDevices: [] });
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>('default');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('default');
  const [volume, setVolume] = useState<number>(70);
  const [rtcStats] = useState<RTCStats | null>(null); // V4: Stats disabled, always null
  // V4: Event logs removed - no data collection
  const [debugMode, setDebugMode] = useState<boolean>(() => localStorage.getItem('webphone_v4_debug_mode') === 'true');

  const userAgentRef = useRef<any>(null);
  const primarySessionRef = useRef<any>(null);
  const consultSessionRef = useRef<any>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringtoneSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const ringtoneContextRef = useRef<AudioContext | null>(null);
  const ringtoneBufferRef = useRef<AudioBuffer | null>(null);
  const sbcHostRef = useRef<string>('sbc15.iatelecom.ca');
  const primaryRemoteStreamRef = useRef<MediaStream | null>(null);
  const consultRemoteStreamRef = useRef<MediaStream | null>(null);
  const consultAutoHoldRef = useRef(false);
  const callStatusRef = useRef<CallStatus>('idle');
  const sanitizeDialString = useCallback((input: string) => input.replace(/[^0-9*#]/g, ''), []);
  // V4: Add event log - NO-OP version, only console logging, no state updates
  const addEventLog = useCallback((message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    // Only console log, don't store in state
    console.log(`[WebPhoneV4] ${type.toUpperCase()}: ${message}`);
  }, []);

  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);

  const attachRemoteAudio = useCallback((stream: MediaStream) => {
    if (!remoteAudioRef.current) return;
    const audioEl = remoteAudioRef.current;
    audioEl.srcObject = stream;
    audioEl.muted = false;
    audioEl.play().then(() => {
      localStorage.setItem('webphone_audio_enabled', 'true');
    }).catch((e: Error) => {
      console.error('[WebPhoneV4] Audio play error:', e.message);
      addEventLog(`Audio play error: ${e.message}`, 'error');
    });
  }, [addEventLog]);

  const clearRemoteAudio = useCallback(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
  }, []);

  const reattachPrimaryRemoteAudio = useCallback(() => {
    if (primaryRemoteStreamRef.current) {
      attachRemoteAudio(primaryRemoteStreamRef.current);
    } else {
      clearRemoteAudio();
    }
  }, [attachRemoteAudio, clearRemoteAudio]);

  const cleanupPrimarySession = useCallback((options?: { terminate?: boolean }) => {
    const shouldTerminate = options?.terminate !== false;
    if (primarySessionRef.current) {
      if (shouldTerminate) {
        try {
          primarySessionRef.current.terminate();
        } catch (error) {
          console.error('[WebPhoneV4] Error terminating primary session:', error);
        }
      }
      primarySessionRef.current = null;
    }
    setCurrentCall(null);
    setCallStatus('idle');
    primaryRemoteStreamRef.current = null;
    clearRemoteAudio();
    if (localAudioRef.current) {
      localAudioRef.current.srcObject = null;
    }
  }, [clearRemoteAudio]);

  const cleanupConsultSession = useCallback((resumePrimary: boolean) => {
    if (consultSessionRef.current) {
      try {
        consultSessionRef.current.terminate();
      } catch (error) {
        console.error('[WebPhoneV4] Error terminating consult session:', error);
      }
      consultSessionRef.current = null;
    }
    setConsultCall(null);
    consultRemoteStreamRef.current = null;
    if (resumePrimary) {
      if (consultAutoHoldRef.current && primarySessionRef.current) {
        try {
          primarySessionRef.current.unhold();
          setIsOnHold(false);
        } catch (error) {
          console.error('[WebPhoneV4] Failed to unhold primary session:', error);
        }
      }
      consultAutoHoldRef.current = false;
      if (primarySessionRef.current) {
        setCallStatus('in-call');
        reattachPrimaryRemoteAudio();
      } else {
        clearRemoteAudio();
      }
    } else {
      consultAutoHoldRef.current = false;
    }
  }, [clearRemoteAudio, reattachPrimaryRemoteAudio]);

  const initiateRefer = useCallback((
    fromConsult: boolean,
    target: string,
    options?: {
      failureStatus?: CallStatus;
      referUri?: string;
      onSuccess?: () => void;
      onFailure?: (reason: string) => void;
    }
  ) => {
    const referSession = fromConsult ? consultSessionRef.current : primarySessionRef.current;
    if (!referSession) {
      const msg = fromConsult ? 'Consult call is not ready to transfer.' : 'No active call to transfer';
      addEventLog(msg, 'error');
      setErrorMessage(msg);
      options?.onFailure?.(msg);
      return;
    }

    if (!primarySessionRef.current) {
      const msg = 'No active call to transfer';
      addEventLog(msg, 'error');
      setErrorMessage(msg);
      options?.onFailure?.(msg);
      return;
    }

    const cleanTarget = sanitizeDialString(target);
    if (!options?.referUri && !cleanTarget) {
      const msg = 'Enter a valid extension to transfer the call.';
      addEventLog('Invalid transfer target', 'error');
      setErrorMessage(msg);
      options?.onFailure?.(msg);
      return;
    }

    const referTarget = options?.referUri || `sip:${cleanTarget}@${sbcHostRef.current}`;
    addEventLog(`Initiating transfer to ${options?.referUri ? 'consult leg' : cleanTarget}`, 'info');
    setErrorMessage(null);
    setCallStatus('transferring');

    const handleFailure = (reason: string) => {
      const fallbackStatus = options?.failureStatus || (primarySessionRef.current ? 'in-call' : 'idle');
      setCallStatus(fallbackStatus);
      setErrorMessage(`Transfer failed: ${reason}`);
      addEventLog(`Transfer failed: ${reason}`, 'error');
      options?.onFailure?.(reason);
    };

    const eventHandlers = {
      requestSucceeded: () => {
        addEventLog(`Transfer to ${cleanTarget} succeeded`, 'success');
        options?.onSuccess?.();
        setDialedNumber('');
        cleanupPrimarySession({ terminate: false });
        cleanupConsultSession(false);
      },
      requestFailed: (e: any) => {
        handleFailure(e?.cause || 'Unknown error');
      },
      requestTimeout: () => {
        handleFailure('Request timed out');
      }
    };

    try {
      referSession.refer(referTarget, { eventHandlers });
    } catch (error: any) {
      handleFailure(error?.message || 'Unable to initiate transfer');
    }
  }, [addEventLog, cleanupConsultSession, cleanupPrimarySession, sanitizeDialString]);

  // Enumerate audio devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const filterDevices = (deviceList: MediaDeviceInfo[]) => {
        const withLabels = deviceList.filter(device => device.label && device.label.trim() !== '');
        const uniqueDevices = withLabels.filter((device, index, self) =>
          index === self.findIndex(d => d.label === device.label)
        );
        return uniqueDevices;
      };

      const inputDevices = filterDevices(devices.filter(device => device.kind === 'audioinput'));
      const outputDevices = filterDevices(devices.filter(device => device.kind === 'audiooutput'));

      setAudioDevices({ inputDevices, outputDevices });
      addEventLog(`Found ${inputDevices.length} microphones and ${outputDevices.length} speakers`, 'info');
    } catch (error) {
      addEventLog('Failed to enumerate devices', 'error');
      console.error('[WebPhoneV4] Failed to enumerate devices:', error);
    }
  }, [addEventLog]);

  // Initialize audio elements - Based on working mobile-dialer-v3.html
  useEffect(() => {
    // Check if user has previously enabled audio
    const audioEnabled = localStorage.getItem('webphone_audio_enabled') === 'true';

    // Remote audio element
    const remoteAudio = new Audio();
    remoteAudio.id = 'webphone-remote-audio-v3';
    remoteAudio.autoplay = true;
    remoteAudio.volume = 0.7;
    remoteAudio.muted = !audioEnabled; // Mute if not enabled yet
    remoteAudio.style.display = 'none';
    document.body.appendChild(remoteAudio);
    remoteAudioRef.current = remoteAudio;

    // Local audio element (for monitoring own voice)
    const localAudio = new Audio();
    localAudio.id = 'webphone-local-audio-v3';
    localAudio.autoplay = true;
    localAudio.muted = true; // Mute local to avoid feedback
    localAudio.style.display = 'none';
    document.body.appendChild(localAudio);
    localAudioRef.current = localAudio;

    // Ringtone using Web Audio API - classic telephone bell sound
    // Generate classic phone ring using Web Audio API for better quality
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const generatePhoneRing = () => {
      const sampleRate = audioContext.sampleRate;
      const duration = 4; // 4 seconds: ring (2s) + silence (2s)
      const buffer = audioContext.createBuffer(1, sampleRate * duration, sampleRate);
      const data = buffer.getChannelData(0);

      // Classic North American ring: 2 seconds on, 2 seconds off
      for (let i = 0; i < sampleRate * duration; i++) {
        const t = i / sampleRate;
        if (t < 2) { // Ring for 2 seconds
          // Dual-tone: 440Hz + 480Hz (classic telephone bell frequencies)
          const freq1 = 440;
          const freq2 = 480;
          // Add envelope for realistic bell decay
          const envelope = Math.exp(-t * 0.5);
          data[i] = envelope * (
            Math.sin(2 * Math.PI * freq1 * t) * 0.5 +
            Math.sin(2 * Math.PI * freq2 * t) * 0.5
          );
        } else {
          data[i] = 0; // Silence for 2 seconds
        }
      }
      return buffer;
    };

    // Store the buffer and context for playback
    const ringBuffer = generatePhoneRing();
    ringtoneContextRef.current = audioContext;
    ringtoneBufferRef.current = ringBuffer;

    addEventLog('Audio elements initialized', 'success');

    return () => {
      if (remoteAudio.parentNode) {
        remoteAudio.parentNode.removeChild(remoteAudio);
      }
      if (localAudio.parentNode) {
        localAudio.parentNode.removeChild(localAudio);
      }
      // Stop any playing ringtone
      if (ringtoneSourceRef.current) {
        try {
          ringtoneSourceRef.current.stop();
        } catch (e) {
          // Already stopped
        }
        ringtoneSourceRef.current = null;
      }
      if (ringtoneContextRef.current) {
        ringtoneContextRef.current.close();
      }
    };
  }, [addEventLog]);

  // Cleanup: Unregister when page is closed/refreshed
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (userAgentRef.current && userAgentRef.current.isRegistered()) {
        console.log('[WebPhoneV4] Page unloading - unregistering SIP user agent');
        try {
          // Send REGISTER with Expires: 0 to unregister immediately
          userAgentRef.current.unregister({
            all: true
          });
          // Stop the user agent
          userAgentRef.current.stop();
        } catch (error) {
          console.error('[WebPhoneV4] Error during cleanup:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also cleanup when component unmounts
      handleBeforeUnload();
    };
  }, []);

  // Play/stop ringtone based on call status using Web Audio API
  useEffect(() => {
    if ((callStatus === 'calling' || callStatus === 'ringing') && ringtoneContextRef.current && ringtoneBufferRef.current) {
      // Stop any existing ringtone
      if (ringtoneSourceRef.current) {
        try {
          ringtoneSourceRef.current.stop();
        } catch (e) {
          // Already stopped
        }
      }

      // Create a new source for the ringtone and play it
      const playRingtone = () => {
        if (!ringtoneContextRef.current || !ringtoneBufferRef.current) return;

        const source = ringtoneContextRef.current.createBufferSource();
        source.buffer = ringtoneBufferRef.current;
        source.loop = true; // Loop the ringtone

        // Create gain node for volume control
        const gainNode = ringtoneContextRef.current.createGain();
        gainNode.gain.value = 0.7; // 70% volume

        source.connect(gainNode);
        gainNode.connect(ringtoneContextRef.current.destination);

        source.start(0);
        ringtoneSourceRef.current = source;

        console.log('[WebPhoneV4] Ringtone started for status:', callStatus);
        addEventLog('Ringtone playing', 'info');
      };

      // Resume AudioContext if suspended (required by browser autoplay policies)
      if (ringtoneContextRef.current.state === 'suspended') {
        ringtoneContextRef.current.resume().then(() => {
          playRingtone();
        }).catch((e: Error) => {
          console.error('[WebPhoneV4] Failed to resume AudioContext:', e);
        });
      } else {
        playRingtone();
      }
    } else {
      // Stop ringtone when not calling/ringing
      if (ringtoneSourceRef.current) {
        try {
          ringtoneSourceRef.current.stop();
          console.log('[WebPhoneV4] Ringtone stopped');
          addEventLog('Ringtone stopped', 'info');
        } catch (e) {
          // Already stopped
        }
        ringtoneSourceRef.current = null;
      }
    }
  }, [callStatus, addEventLog]);

  // Update call duration timer
  useEffect(() => {
    if ((callStatus === 'in-call' || callStatus === 'consulting' || callStatus === 'consult-connected') && currentCall) {
      const interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - currentCall.startTime.getTime()) / 1000);
        setCurrentCall(prev => prev ? { ...prev, duration: elapsed } : null);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [callStatus, currentCall?.startTime]);

  // Toggle debug mode
  const toggleDebugMode = useCallback(() => {
    setDebugMode(prev => {
      const newValue = !prev;
      localStorage.setItem('webphone_debug_mode', String(newValue));
      if (newValue) {
        JsSIP.debug.enable('JsSIP:*');
        addEventLog('Debug mode enabled', 'info');
      } else {
        JsSIP.debug.disable();
        addEventLog('Debug mode disabled', 'info');
      }
      return newValue;
    });
  }, [addEventLog]);

  // Connect to WebRTC
  const connect = useCallback(async (credentials: WebPhoneCredentials) => {
    try {
      console.log('[WebPhoneV4] Connecting with credentials:', credentials);
      addEventLog(`Connecting to ${credentials.sbcServers[0].host}...`, 'info');
      setConnectionStatus('connecting');

      // Enable debug if needed
      if (debugMode) {
        JsSIP.debug.enable('JsSIP:*');
      }

      const wsUri = credentials.sbcServers[0].wsUri;
      sbcHostRef.current = credentials.sbcServers[0].host;

      const socket = new JsSIP.WebSocketInterface(wsUri);

      const configuration = {
        sockets: [socket],
        uri: `sip:${credentials.username}@${credentials.sbcServers[0].host}`,
        password: credentials.password,
        display_name: credentials.displayName || credentials.extension,
        register: true,
        session_timers: false,
        register_expires: 600,
      };

      const ua = new JsSIP.UA(configuration);

      // UA event handlers
      ua.on('connecting', () => {
        console.log('[WebPhoneV4] Connecting to WebSocket...');
        addEventLog('Connecting to WebSocket...', 'info');
      });

      ua.on('connected', () => {
        console.log('[WebPhoneV4] WebSocket connected');
        addEventLog('WebSocket connected', 'success');
        setConnectionStatus('connected');
      });

      ua.on('disconnected', () => {
        console.log('[WebPhoneV4] WebSocket disconnected');
        addEventLog('Disconnected from server', 'error');
        setConnectionStatus('disconnected');
      });

      ua.on('registered', () => {
        console.log('[WebPhoneV4] Successfully registered');
        addEventLog(`Registered as ${credentials.extension}`, 'success');
        setConnectionStatus('registered');
      });

      ua.on('registrationFailed', (e: any) => {
        console.error('[WebPhoneV4] Registration failed:', e);
        addEventLog(`Registration failed: ${e.cause}`, 'error');
        setConnectionStatus('error');
        setErrorMessage('Registration failed');
      });

      ua.on('newRTCSession', (e: any) => {
        const session = e.session;

        if (session.direction === 'incoming') {
          const callerNumber = session.remote_identity.uri.user;
          console.log('[WebPhoneV4] === INCOMING CALL RECEIVED ===');
          console.log('[WebPhoneV4] Caller:', callerNumber);
          console.log('[WebPhoneV4] primarySessionRef.current exists?', !!primarySessionRef.current);

          // Check if we're already on a call - silently reject with busy
          // Only check primarySessionRef, not callStatus (which is stale in this callback)
          if (primarySessionRef.current) {
            // Verify the session is actually active before rejecting
            const currentStatus = primarySessionRef.current.status;
            console.log('[WebPhoneV4] Current session status:', currentStatus);

            // Only reject if current session is actually active (not ended/terminated)
            // JsSIP statuses: 'STATUS_NULL', 'STATUS_INVITE_SENT', 'STATUS_1XX_RECEIVED', 'STATUS_INVITE_RECEIVED',
            // 'STATUS_WAITING_FOR_ANSWER', 'STATUS_ANSWERED', 'STATUS_WAITING_FOR_ACK', 'STATUS_CANCELED',
            // 'STATUS_TERMINATED', 'STATUS_CONFIRMED'
            const activeStatuses = ['STATUS_INVITE_SENT', 'STATUS_1XX_RECEIVED', 'STATUS_INVITE_RECEIVED',
                                   'STATUS_WAITING_FOR_ANSWER', 'STATUS_ANSWERED', 'STATUS_WAITING_FOR_ACK',
                                   'STATUS_CONFIRMED'];

            if (activeStatuses.includes(String(currentStatus))) {
              console.log('[WebPhoneV4] Session is active, rejecting incoming call with busy');
              addEventLog(`Busy: Rejected call from ${callerNumber}`, 'warning');
              // Silently reject with 486 Busy Here
              session.terminate({
                status_code: 486,
                reason_phrase: 'Busy Here'
              });
              return;
            } else {
              // Session is terminated/canceled, clear it
              console.log('[WebPhoneV4] Current session is not active, clearing stale reference');
              primarySessionRef.current = null;
            }
          }

          console.log('[WebPhoneV4] Accepting incoming call...');
          addEventLog(`Incoming call from ${callerNumber}`, 'info');

          // Update UI state to show incoming call
          console.log('[WebPhoneV4] Setting call status to ringing...');
          setCallStatus('ringing');
          setDialedNumber(callerNumber);

          // Set currentCall so the Answer button appears
          console.log('[WebPhoneV4] Setting currentCall...');
          setCurrentCall({
            session,
            direction: 'incoming',
            remoteNumber: callerNumber,
            startTime: new Date(),
            duration: 0,
            status: 'ringing'
          });

          console.log('[WebPhoneV4] Setting primarySessionRef...');
          primarySessionRef.current = session;

          console.log('[WebPhoneV4] Setting up session handlers...');
          setupSessionHandlers(session, 'incoming', callerNumber, 'primary');

          console.log('[WebPhoneV4] === INCOMING CALL SETUP COMPLETE ===');
          // Don't auto-answer - wait for user to click Answer button
        }
      });

      userAgentRef.current = ua;
      ua.start();

      addEventLog('User agent started', 'success');
    } catch (error) {
      console.error('[WebPhoneV4] Connect failed:', error);
      addEventLog('Connection failed', 'error');
      setConnectionStatus('error');
      setErrorMessage('Failed to connect');
    }
  }, [debugMode, addEventLog]);

  // Setup session handlers - Based on working mobile-dialer-v3.html
  const setupSessionHandlers = useCallback((
    session: any,
    direction: 'incoming' | 'outgoing',
    number: string,
    sessionType: 'primary' | 'consult' = 'primary'
  ) => {
    const isPrimary = sessionType === 'primary';
    const updateCallState = isPrimary ? setCurrentCall : setConsultCall;
    const remoteStreamRef = isPrimary ? primaryRemoteStreamRef : consultRemoteStreamRef;

    console.log('[WebPhoneV4] Setting up session handlers for', sessionType, direction, number);

    session.on('peerconnection', (e: any) => {
      addEventLog(`[${sessionType}] Peer connection established`, 'info');
      const peerconnection = e.peerconnection;

      peerconnection.ontrack = (event: RTCTrackEvent) => {
        if (event.streams && event.streams[0] && event.track.kind === 'audio') {
          remoteStreamRef.current = event.streams[0];
          if (isPrimary) {
            if (callStatusRef.current !== 'consulting' && callStatusRef.current !== 'consult-connected') {
              attachRemoteAudio(event.streams[0]);
            }
          } else {
            attachRemoteAudio(event.streams[0]);
          }
        }
      };

      peerconnection.onaddstream = (event: any) => {
        remoteStreamRef.current = event.stream;
        if (isPrimary) {
          if (callStatusRef.current !== 'consulting' && callStatusRef.current !== 'consult-connected') {
            attachRemoteAudio(event.stream);
          }
        } else {
          attachRemoteAudio(event.stream);
        }
      };

      const senders = peerconnection.getSenders();
      const audioSender = senders.find((sender: RTCRtpSender) => sender.track && sender.track.kind === 'audio');
      if (audioSender && audioSender.track && localAudioRef.current) {
        const localStream = new MediaStream([audioSender.track]);
        localAudioRef.current.srcObject = localStream;
      }
    });

    session.on('confirmed', () => {
      addEventLog(`[${sessionType}] Call confirmed`, 'success');
      updateCallState({
        session,
        direction,
        remoteNumber: number,
        startTime: new Date(),
        duration: 0,
        status: 'connected'
      });
      if (isPrimary) {
        setCallStatus('in-call');
        reattachPrimaryRemoteAudio();
      } else {
        setCallStatus('consult-connected');
      }
    });

    session.on('progress', () => {
      addEventLog(`[${sessionType}] Call in progress`, 'info');
      if (isPrimary) {
        setCallStatus('ringing');
      } else {
        setCallStatus('consulting');
      }
    });

    session.on('failed', (e: any) => {
      const cause = e?.cause || 'Unknown';
      if (!isPrimary && cause === 'Canceled' && (callStatusRef.current === 'consulting' || callStatusRef.current === 'consult-connected')) {
        addEventLog('[consult] Call cancelled', 'info');
        cleanupConsultSession(true);
        return;
      }
      addEventLog(`[${sessionType}] Call failed: ${cause}`, 'error');
      if (isPrimary) {
        cleanupPrimarySession();
      } else {
        cleanupConsultSession(true);
        setErrorMessage(`Consult call failed: ${cause}`);
      }
    });

    session.on('ended', (e: any) => {
      addEventLog(`[${sessionType}] Call ended: ${e.cause}`, 'info');
      if (isPrimary) {
        cleanupPrimarySession();
      } else {
        cleanupConsultSession(true);
      }
    });
  }, [addEventLog, attachRemoteAudio, cleanupConsultSession, cleanupPrimarySession, reattachPrimaryRemoteAudio]);

  // Make a call - Based on working mobile-dialer-v3.html
  const makeCall = useCallback((number: string) => {
    if (!userAgentRef.current) {
      console.error('[WebPhoneV4] User agent not initialized');
      addEventLog('Not connected to server', 'error');
      return;
    }

    const sipUri = `sip:${number}@${sbcHostRef.current}`;
    console.log('[WebPhoneV4] Calling:', sipUri);
    addEventLog(`Calling ${number}...`, 'info');
    setCallStatus('calling');

    // CRITICAL: Event handlers must be passed BEFORE call()
    const eventHandlers = {
      progress: () => {
        console.log('[WebPhoneV4] Call is in progress');
      },
      failed: (e: any) => {
        console.log('[WebPhoneV4] Call failed:', e);
      },
      ended: (e: any) => {
        console.log('[WebPhoneV4] Call ended:', e);
      },
      accepted: () => {
        console.log('[WebPhoneV4] Call accepted');
      },
    };

    const options = {
      eventHandlers,
      mediaConstraints: {
        audio: { channelCount: 1 },
        video: false
      },
      pcConfig: {
        iceServers: [
          { urls: ['stun:stun.l.google.com:19302'] }
        ]
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false
      }
    };

    const session = userAgentRef.current.call(sipUri, options);
    primarySessionRef.current = session;

    // Set currentCall immediately so UI can check direction
    setCurrentCall({
      session,
      direction: 'outgoing',
      remoteNumber: number,
      startTime: new Date(),
      duration: 0,
      status: 'ringing'
    });

    setupSessionHandlers(session, 'outgoing', number, 'primary');
  }, [setupSessionHandlers, addEventLog]);

  // Disconnect
  const disconnect = useCallback(() => {
    cleanupConsultSession(false);
    if (primarySessionRef.current) {
      primarySessionRef.current.terminate();
      primarySessionRef.current = null;
    }
    if (userAgentRef.current) {
      userAgentRef.current.stop();
      userAgentRef.current = null;
    }
    addEventLog('Disconnected', 'info');
    setConnectionStatus('disconnected');
    setCallStatus('idle');
    setCurrentCall(null);
  }, [addEventLog, cleanupConsultSession]);

  // Answer call
  const answerCall = useCallback(() => {
    if (primarySessionRef.current) {
      console.log('[WebPhoneV4] Answering incoming call');
      addEventLog('Answering call...', 'info');

      // Immediately change status to stop ringtone
      setCallStatus('in-call');

      primarySessionRef.current.answer({
        mediaConstraints: {
          audio: { channelCount: 1 },
          video: false
        },
        pcConfig: {
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302'] }
          ]
        },
        rtcAnswerConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        }
      });
    }
  }, [addEventLog]);

  // Hang up
  const hangUp = useCallback(() => {
    if (callStatus === 'consulting' || callStatus === 'consult-connected') {
      cleanupConsultSession(true);
      addEventLog('Consult call cancelled', 'info');
      return;
    }
    if (primarySessionRef.current) {
      primarySessionRef.current.terminate();
      addEventLog('Call ended', 'info');
    }
  }, [callStatus, cleanupConsultSession, addEventLog]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (primarySessionRef.current) {
      if (isMuted) {
        primarySessionRef.current.unmute();
        addEventLog('Microphone unmuted', 'info');
      } else {
        primarySessionRef.current.mute();
        addEventLog('Microphone muted', 'info');
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted, addEventLog]);

  // Toggle hold
  const toggleHold = useCallback(() => {
    if (primarySessionRef.current) {
      if (isOnHold) {
        primarySessionRef.current.unhold();
        addEventLog('Call resumed', 'info');
      } else {
        primarySessionRef.current.hold();
        addEventLog('Call on hold', 'info');
      }
      setIsOnHold(!isOnHold);
    }
  }, [isOnHold, addEventLog]);

  // Add digit to dialed number
  const addDigit = useCallback((digit: string) => {
    setDialedNumber(prev => prev + digit);
    // Send DTMF if in call
    const targetSession = (callStatus === 'consulting' || callStatus === 'consult-connected')
      ? consultSessionRef.current
      : primarySessionRef.current;
    if (targetSession && (callStatus === 'in-call' || callStatus === 'consulting' || callStatus === 'consult-connected')) {
      targetSession.sendDTMF(digit);
      addEventLog(`DTMF: ${digit}`, 'info');
    }
  }, [callStatus, addEventLog]);

  const startConsultCall = useCallback((target: string) => {
    if (!primarySessionRef.current && currentCall?.session) {
      primarySessionRef.current = currentCall.session;
    }

    if (!primarySessionRef.current) {
      const msg = 'You need an active call before starting an attended transfer.';
      setErrorMessage(msg);
      addEventLog(msg, 'error');
      return;
    }

    if (consultSessionRef.current) {
      setErrorMessage('A consult call is already active.');
      return;
    }

    const cleanTarget = sanitizeDialString(target);
    if (!cleanTarget) {
      setErrorMessage('Enter a valid extension to consult.');
      return;
    }

    if (!userAgentRef.current) {
      setErrorMessage('WebPhone is not connected.');
      return;
    }

    consultAutoHoldRef.current = !isOnHold;
    if (consultAutoHoldRef.current) {
      try {
        primarySessionRef.current.hold();
        setIsOnHold(true);
      } catch (error) {
        console.error('[WebPhoneV4] Failed to hold primary call:', error);
      }
    }

    const sipUri = `sip:${cleanTarget}@${sbcHostRef.current}`;
    addEventLog(`Starting consult call to ${cleanTarget}`, 'info');
    setCallStatus('consulting');

    const session = userAgentRef.current.call(sipUri, {
      mediaConstraints: { audio: { channelCount: 1 }, video: false },
      pcConfig: { iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }] },
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
    });

    consultSessionRef.current = session;
    setConsultCall({
      session,
      direction: 'outgoing',
      remoteNumber: cleanTarget,
      startTime: new Date(),
      duration: 0,
      status: 'ringing'
    });

    setupSessionHandlers(session, 'outgoing', cleanTarget, 'consult');
  }, [addEventLog, isOnHold, sanitizeDialString, setupSessionHandlers]);

  const cancelConsultCall = useCallback(() => {
    cleanupConsultSession(true);
    addEventLog('Consult call cancelled', 'info');
  }, [cleanupConsultSession, addEventLog]);

  const completeAttendedTransfer = useCallback(() => {
    const consultSession = consultSessionRef.current;
    if (!consultSession || !consultCall?.remoteNumber) {
      setErrorMessage('No consult call to transfer.');
      return;
    }
    if (!primarySessionRef.current) {
      setErrorMessage('No active primary call to transfer.');
      return;
    }

    const dialog = consultSession._dialog || consultSession.dialog;
    const dialogId = dialog?.id;
    const referUriBase = consultSession.remote_identity?.uri?.toString();
    if (!dialogId || !dialogId.call_id || !dialogId.local_tag || !dialogId.remote_tag || !referUriBase) {
      setErrorMessage('Consult call is not ready for transfer yet.');
      return;
    }

    const replacesValue = `${dialogId.call_id};to-tag=${dialogId.remote_tag};from-tag=${dialogId.local_tag}`;
    const referUri = `${referUriBase}?Replaces=${encodeURIComponent(replacesValue)}`;

    initiateRefer(true, consultCall.remoteNumber, {
      referUri,
      failureStatus: 'consult-connected',
      onSuccess: () => addEventLog('Attended transfer completed', 'success'),
      onFailure: () => setCallStatus('consult-connected'),
    });
  }, [consultCall, initiateRefer, addEventLog]);

  const transferCall = useCallback((target: string) => {
    initiateRefer(false, target);
  }, [initiateRefer]);

  // Delete last digit (new feature from mobile-dialer-v3)
  const deleteDigit = useCallback(() => {
    setDialedNumber(prev => prev.slice(0, -1));
  }, []);

  // Clear number
  const clearNumber = useCallback(() => {
    setDialedNumber('');
  }, []);

  // Change speaker
  const changeSpeaker = useCallback(async (deviceId: string) => {
    if (remoteAudioRef.current && typeof (remoteAudioRef.current as any).setSinkId === 'function') {
      try {
        await (remoteAudioRef.current as any).setSinkId(deviceId);
        setSelectedSpeaker(deviceId);
        addEventLog('Speaker changed', 'success');
      } catch (error) {
        console.error('[WebPhoneV4] Failed to change speaker:', error);
        addEventLog('Failed to change speaker', 'error');
      }
    }
  }, [addEventLog]);

  // Change microphone
  const changeMicrophone = useCallback((deviceId: string) => {
    setSelectedMicrophone(deviceId);
    addEventLog('Microphone changed', 'success');
  }, [addEventLog]);

  // Change volume
  const changeVolume = useCallback((newVolume: number) => {
    setVolume(newVolume);
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = newVolume / 100;
    }
  }, []);

  // Test speaker
  const testSpeaker = useCallback(async () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuAzvLZiTYIGGS57OihUBEJTqTh7q9iHAU7k9nzwokkBR98zPDhlkMLD1is5/O0ZBEFI4rU9MCENQoSTKPj6rFkHQQ5j9PyxoolBCd7yvDjmEYMDlam5O6xaRwGN4/R88KHJgQlec3y4ppHCxBZqufvtmYdBTWK0/PEiCUFKnvL8OGZRgsQWKzm77RmHAY4jtHyw4clBSt7yvDglUAKElas5e+xZhwGOY/T8sOIJAQresy/4JtGDBBXrOTwsWcdBTaM0fPAhiQFLH3M8OGaRQsRV6vl77Jm');
    audio.volume = volume / 100;
    try {
      if (typeof (audio as any).setSinkId === 'function') {
        await (audio as any).setSinkId(selectedSpeaker);
      }
      audio.play();
      addEventLog('Testing speaker...', 'info');
    } catch (error) {
      console.error('[WebPhoneV4] Failed to test speaker:', error);
      addEventLog('Failed to test speaker', 'error');
    }
  }, [volume, selectedSpeaker, addEventLog]);

  return {
    connectionStatus,
    callStatus,
    currentCall,
    consultCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    audioDevices,
    selectedSpeaker,
    selectedMicrophone,
    volume,
    rtcStats, // V4: Stats disabled, always null
    // V4: eventLogs removed - no data collection
    debugMode,
    connect,
    disconnect,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    startConsultCall,
    cancelConsultCall,
    completeAttendedTransfer,
    transferCall,
    addDigit,
    deleteDigit, // NEW: Delete last digit instead of clear all
    clearNumber,
    changeSpeaker,
    changeMicrophone,
    changeVolume,
    enumerateDevices,
    testSpeaker,
    toggleDebugMode,
  };
}
